import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { filter, publish } from 'rxjs/operators';

import { GridOptions, ColDef, IDateFilterParams, GridApi } from "ag-grid-community";


import { dateComparator, dateFilterComparator } from "../../common/components/data-table/comparators/data-table.comparators";
import { RouterLinkRendererComponent } from "../../common/components/ag-grid/router-link-renderer.component";

import { DateService } from "../../common/services/date.service";
import { LettersService } from "../letters.service";
import { SubHeaderService } from "../../core/services/sub-header.service";

import { LetterSearchCriteria, LetterStatus, LetterSearchOrderDirection, Letter, LetterStatusUpdateRequest } from "../letters.model";
import { SubHeader } from "../../core/models/sub-header.model";

import { GridFormatterService } from "../../common/services/grid-formatter.service";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { DatePipe } from "@angular/common";
import { TabComponent } from "../../common/components/tabs/tab.component";
import { LoaderService } from "../../common/services/loader.service";
import { AuthService } from "../../services/auth.service";
import { LunaClaimTypes, LunaClaimValues } from "../../models/user-identity.model";


@Component({
    selector: 'app-mail-list',
    templateUrl: './mail-list.component.html',
    styleUrls: ["./mail-list.component.scss"],
    standalone: false
})
export class MailListComponent implements OnInit, OnDestroy {

    data: Letter[];
    sentData: Letter[];
    returnedData: Letter[];
    deliveredData: Letter[];
    updatedData: Letter[] = [];

    possibleLetterStatuses: number[] = [ 1, 2, 3 ];

	gridOptions: GridOptions;
	gridApi?: GridApi;
	sentGridOptions: GridOptions;
	sentGridApi?: GridApi;
	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;
	deliveredGridOptions: GridOptions;
	deliveredGridApi?: GridApi;


    currentDate: Date = new Date();

    toDate: Date | null;
    fromDate: Date | null;
    searchOngoing: boolean = false;
    mailStatusHasChanged: boolean = false;
    canEditStatus: boolean = false;

    placeholder: string = DateService.DateFormat;

    subscriptions: Subscription[] = [];

    searchCriteria = {
        trackingNumber: "",
        inStatuses: [ LetterStatus.Sent, LetterStatus.Delivered, LetterStatus.Returned ],
        orderByPropertyName: "SentDate",
        orderDirection : LetterSearchOrderDirection.Descending
    } as LetterSearchCriteria;

    statusEnum = LetterStatus;

    subHeader = new SubHeader({
        title: "Sent Mailing", buttons: []
    });

    get trackingNumber(): string {
        return this.searchCriteria.trackingNumber;
    }

    set trackingNumber(value: string) {
        this.searchCriteria.trackingNumber = value;
    }

    constructor(
        public readonly router: Router,
        public readonly authService: AuthService,
        public readonly subheaderService: SubHeaderService,
        public readonly lettersService: LettersService,
        public readonly dateService: DateService,
        private readonly formatterService: GridFormatterService,
        public readonly datePipe: DatePipe,
        public readonly loader: LoaderService,
    ) {
        this.canEditStatus = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.EditLetterStatus).length > 0;
    }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subHeader);
        this.setDefaultDates();
        this.searchCriteria.endDate = this.toDate;
        this.searchCriteria.startDate = this.fromDate;
        this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, true);
		this.gridOptions.context = { componentParent: this };
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.sentGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, true);
		this.sentGridOptions.context = { componentParent: this };
		this.sentGridOptions.onGridReady = (params) => { this.sentGridApi = params.api; };
        this.returnedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, true);
		this.returnedGridOptions.context = { componentParent: this };
		this.returnedGridOptions.onGridReady = (params) => { this.returnedGridApi = params.api; };
        this.deliveredGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, true);
        this.deliveredGridOptions.context = { componentParent: this };
		this.deliveredGridOptions.onGridReady = (params) => { this.deliveredGridApi = params.api; };

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.forEach(s => s.unsubscribe());
        });

        this.search();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    blankFieldFormatter(component: any, field: string) {
        return (obj) => { return (obj.data[field] && obj.data[field].length > 0) ? obj.data[field] : "Unknown" };
    }

    createColumnDefinitions(): ColDef[] {

        let recipientColumn = {
            headerName: "Recipient",
            headerTooltip: "Recipient",
            field: "recipientName",
            tooltipField: "recipientName",
            filter: "agTextColumnFilter",
            cellRenderer: RouterLinkRendererComponent,
            cellRendererParams: {
                idField: "id",
                textField: "recipientName",
                url: "/letters"
            },
            minWidth: 200
        } as ColDef;

        let organizationNameColumn = {
            headerName: "Organization",
            headerTooltip: "Organization",
            field: "organizationName",
            tooltipValueGetter: this.blankFieldFormatter(this, "organizationName"),
            valueGetter: this.blankFieldFormatter(this, "organizationName"),
            filter: "agTextColumnFilter",
            minWidth: 200
        } as ColDef;

        let descriptionColumn = {
            headerName: "Description",
            headerTooltip: "Description",
            field: "title",
            tooltipValueGetter: this.blankFieldFormatter(this, "title"),
            valueGetter: this.blankFieldFormatter(this, "title"),
            filter: "agTextColumnFilter",
            minWidth: 200
        } as ColDef;

        let sentColumn = {
            headerName: "Sent",
            headerTooltip: "Sent",
            field: "sentDate",
            tooltipValueGetter: this.formatterService.formatDate("sentDate"),
            valueGetter: this.formatterService.formatDate("sentDate"),
            comparator: dateComparator,
            filter: "agDateColumnFilter",
            filterParams: { browserDatePicker: true, comparator: dateFilterComparator, includeTime: false, buttons: ['reset'] } as IDateFilterParams,
            sort: "desc",
            sortingOrder: ["asc", null, "desc"],
            minWidth: 100
        } as ColDef;

        let mailingTypeColumn = {
            headerName: "Mailing Type",
            headerTooltip: "Mailing Type",
            field: "mailType",
            tooltipField: "mailType",
            filter: 'agSetColumnFilter',
            minWidth: 110
        } as ColDef;

        let nonEditableStatusColumn = {
            headerName: "Status",
            headerTooltip: "Status",
            field: "trackingStatus",
            tooltipValueGetter: this.statusFormatter(this),
            valueGetter: this.statusFormatter(this),
            valueSetter: this.setStatus,
            filter: 'agSetColumnFilter',
            minWidth: 100
        } as ColDef;

        let editableStatusColumn = {
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                cellRenderer: this.renderUserDropdownOptions,
                values: this.possibleLetterStatuses
            },
            cellRenderer: this.renderStatusView,
            onCellValueChanged: this.cellValueChanged,
            editable: true,
            headerName: "Status",
            headerTooltip: "Status",
            field: "trackingStatus",
            tooltipValueGetter: this.statusFormatter(this),
            valueGetter: this.statusFormatter(this),
            valueSetter: this.setStatus,
            filter: 'agSetColumnFilter',
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            minWidth: 100
        } as ColDef;

        let trackingStatusColumn = {
            headerName: "Tracking Status",
            headerTooltip: "Tracking Status",
            field: "trackingStatus",
            tooltipValueGetter: this.trackingStatusFormatter(this),
            valueGetter: this.trackingStatusFormatter(this),
            filter: 'agSetColumnFilter',
            minWidth: 130
        } as ColDef;

        let trackingNumberColumn = {
            headerName: "Tracking Number",
            headerTooltip: "Tracking Number",
            field: "trackingNumber",
            tooltipValueGetter: this.trackingNumberFormatter(this),
            valueGetter: this.trackingNumberFormatter(this),
            filter: "agTextColumnFilter",
            minWidth: 200
        } as ColDef;

        if (this.canEditStatus)
            return [recipientColumn, organizationNameColumn, descriptionColumn, sentColumn, mailingTypeColumn, editableStatusColumn, trackingStatusColumn, trackingNumberColumn];
        else
            return [recipientColumn, organizationNameColumn, descriptionColumn, sentColumn, mailingTypeColumn, nonEditableStatusColumn, trackingStatusColumn, trackingNumberColumn];
    }

    goHome(): void {
        this.router.navigate(["home"]);
    }

    reset(): void {
        this.setDefaultDates();
        this.searchCriteria.endDate = this.toDate;
        this.searchCriteria.startDate = this.fromDate;
        this.searchCriteria.trackingNumber = "";
        this.search();
    }

    search(): void {
        if (!this.searchOngoing) {
            this.searchOngoing = true;
            this.data = [];
            this.sentData = [];
            this.returnedData = [];
            this.deliveredData = [];
            this.loader.toggleLoader(true);
            let letterDate$ = this.lettersService.find(this.searchCriteria).pipe(finalize(() => {
                this.subscriptions.push(letterDate$);
            })).subscribe((foundSentMail) => {
                this.searchOngoing = false;
                this.loader.toggleLoader(false);
                this.data = foundSentMail;
                this.sentData = foundSentMail.filter(a => a.status == LetterStatus.Sent);
                this.returnedData = foundSentMail.filter(a => a.status == LetterStatus.Returned);
                this.deliveredData = foundSentMail.filter(a => a.status == LetterStatus.Delivered);

                this.onTabClicked(null);
            }, (error) => {
                this.searchOngoing = false;
                this.loader.toggleLoader(false);
                console.error(error) 
            });
        }
    }

    setDefaultDates(): void {
        this.fromDate = this.dateService.generateDateWithOffset(0, 0, -16);
        this.toDate = this.currentDate;
    }

    fromDateChange(evt: Date): void {
        this.fromDate = evt;
        this.searchCriteria.startDate = evt;
    }

    toDateChange(evt: Date): void {
        this.toDate = evt;
        this.searchCriteria.endDate = evt;
    }

    onTabClicked(tab: TabComponent): void {
        if ((tab == null || tab.title == "All") && this.gridApi)
            this.gridApi.sizeColumnsToFit();
        if ((tab == null || tab.title == "Sent") && this.sentGridApi)
            this.sentGridApi.sizeColumnsToFit();
        if ((tab == null || tab.title == "Returned") && this.returnedGridApi)
            this.returnedGridApi.sizeColumnsToFit();
        if ((tab == null || tab.title == "Delivered") && this.deliveredGridApi)
            this.deliveredGridApi.sizeColumnsToFit();
    }

    renderUserDropdownOptions(params: any): string {
        let statusEnum = LetterStatus;
        let returnValue = "Unknown";
        if (typeof (params.value) === 'number')
            returnValue = params && params.value ? statusEnum[params.value] : "Unknown";
        else
            returnValue = params.value;
        return returnValue;
    }

    hasMailStatusChanged(): boolean {
        return this.mailStatusHasChanged;
    }

    setStatus(params): any {
        let result: boolean = false;
        if (params && params.newValue) {
            params.data.status = params.newValue;

            if (params.context && params.context.componentParent) {
                params.context.componentParent.mailStatusHasChanged = true;

                let matchingItems = params.context.componentParent.updatedData.filter(a => a.id == params.data.id);
                if (matchingItems.length == 0)
                    params.context.componentParent.updatedData.push(params.data);
                else
                    matchingItems[0].status = params.newValue;
            }

            result = true;
        }
        return result;
    }


    statusFormatter(component: any) {
        return (obj) => component.statusEnum[obj.data.status];
    }

    renderStatusView(params: any): string {
        let returnValue: string = "Unknown";
        returnValue = params.value;
        return returnValue;
    }

    cellValueChanged(params): void {
        if (params.api) {
            params.api.refreshCells();
        }
    }

    saveLetterChanges(): void {
        if(this.updatedData && this.updatedData.length > 0) {
            let statusChanges = this.updatedData.map(a => ({ letterId: a.id, status: a.status } as LetterStatusUpdateRequest));
            this.loader.toggleLoader(true);

            let updateLetterStatuses$ = this.lettersService.updateLetterStatus(statusChanges).pipe(finalize(() => {
                this.subscriptions.push(updateLetterStatuses$);
            })).subscribe((success) => {
                this.updatedData = [];
                this.search();
                this.onTabClicked(null);
            }, (error) => {
                this.searchOngoing = false;
                this.loader.toggleLoader(false);
                console.error(error)
            });
        }
    }

    trackingStatusFormatter(component: any) {
        return (obj) => { return (obj.data.trackingStatus != null && obj.data.trackingStatus != "") ? obj.data.trackingStatus : "N/A"; };
    }

    trackingNumberFormatter(component: any) {
        return (obj) => { return (obj.data.isCertified) ? obj.data.trackingNumber : "N/A"; };
    }

    downloadAllReport(): void {
        let fileName = `CompleteMailList_${this.datePipe.transform(this.fromDate, 'yyyy-MM-dd')} _${this.datePipe.transform(this.toDate, 'yyyy-MM-dd')}`;
        this.formatterService.exportDataToExcel(fileName, "Complete Mail List", this.gridApi, false);
    }

    downloadSentReport(): void {
        let fileName = `SentMailList_${this.datePipe.transform(this.fromDate, 'yyyy-MM-dd')} _${this.datePipe.transform(this.toDate, 'yyyy-MM-dd')}`;
        this.formatterService.exportDataToExcel(fileName, "Sent Mail List", this.sentGridApi, false);
    }

    downloadReturnedReport(): void {
        let fileName = `ReturnedMailList_${this.datePipe.transform(this.fromDate, 'yyyy-MM-dd')} _${this.datePipe.transform(this.toDate, 'yyyy-MM-dd')}`;
        this.formatterService.exportDataToExcel(fileName, "Returned Mail List", this.returnedGridApi, false);
    }

    downloadDeliveredReport(): void {
        let fileName = `DeliveredMailList_${this.datePipe.transform(this.fromDate, 'yyyy-MM-dd')} _${this.datePipe.transform(this.toDate, 'yyyy-MM-dd')}`;
        this.formatterService.exportDataToExcel(fileName, "Delivered Mail List", this.deliveredGridApi, false);
    }

}
